// src/contexts/DialogContext.tsx
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useTheme } from './ThemeContext';
import AlertDialog, { type AlertDialogVariant } from '../components/common/AlertDialog';
import { logger } from '@/utils/logger';

export interface DialogOptions {
  variant?: AlertDialogVariant;
  title?: string;
}

interface DialogState {
  message: string;
  options: DialogOptions;
  mode: 'alert' | 'confirm';
  resolve: (value: boolean) => void;
}

interface DialogContextValue {
  showAlert: (message: string, options?: DialogOptions) => Promise<void>;
  showConfirm: (message: string, options?: DialogOptions) => Promise<boolean>;
}

const DialogContext = createContext<DialogContextValue | undefined>(undefined);

// Singleton bridge para callers fora da árvore React (ex: api.ts interceptor)
let dialogApi: DialogContextValue | null = null;

export const showAlert = (message: string, options?: DialogOptions): Promise<void> => {
  if (!dialogApi) {
    logger.warn('showAlert chamado antes do DialogProvider montar; fallback para alert() nativo');
    alert(message);
    return Promise.resolve();
  }
  return dialogApi.showAlert(message, options);
};

export const showConfirm = (message: string, options?: DialogOptions): Promise<boolean> => {
  if (!dialogApi) {
    logger.warn('showConfirm chamado antes do DialogProvider montar; fallback para window.confirm() nativo');
    return Promise.resolve(window.confirm(message));
  }
  return dialogApi.showConfirm(message, options);
};

export const DialogProvider = ({ children }: { children: ReactNode }) => {
  const { theme } = useTheme();
  const [queue, setQueue] = useState<DialogState[]>([]);

  const showAlertImpl = useCallback(
    (message: string, options?: DialogOptions): Promise<void> =>
      new Promise((resolve) => {
        setQueue((q) => [
          ...q,
          { message, options: options ?? {}, mode: 'alert', resolve: () => resolve() },
        ]);
      }),
    []
  );

  const showConfirmImpl = useCallback(
    (message: string, options?: DialogOptions): Promise<boolean> =>
      new Promise((resolve) => {
        setQueue((q) => [
          ...q,
          { message, options: options ?? {}, mode: 'confirm', resolve },
        ]);
      }),
    []
  );

  const current = queue[0] ?? null;

  const handleConfirm = () => {
    if (current) {
      current.resolve(true);
      setQueue((q) => q.slice(1));
    }
  };

  const handleCancel = () => {
    if (current) {
      current.resolve(false);
      setQueue((q) => q.slice(1));
    }
  };

  const value = useMemo<DialogContextValue>(
    () => ({ showAlert: showAlertImpl, showConfirm: showConfirmImpl }),
    [showAlertImpl, showConfirmImpl]
  );

  useEffect(() => {
    dialogApi = value;
    return () => {
      if (dialogApi === value) {
        dialogApi = null;
      }
    };
  }, [value]);

  return (
    <DialogContext.Provider value={value}>
      {children}
      {current && (
        <AlertDialog
          isOpen
          theme={theme}
          variant={current.options.variant ?? 'info'}
          title={current.options.title}
          message={current.message}
          mode={current.mode}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </DialogContext.Provider>
  );
};

export const useAlertDialog = (): DialogContextValue => {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useAlertDialog deve ser usado dentro de um DialogProvider');
  return ctx;
};

// Dev-only debug exports — strip em prod via import.meta.env.DEV
if (import.meta.env.DEV && typeof window !== 'undefined') {
  const w = window as Window & {
    showAlert?: typeof showAlert;
    showConfirm?: typeof showConfirm;
  };
  w.showAlert = showAlert;
  w.showConfirm = showConfirm;
}
